import { useState, useMemo, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateVisitRequest, Patient, Visit, VisitType } from '@yui-receipt/shared'
import { api, ApiClientError } from '@/lib/api'
import { useAuth } from '@/store/auth'
import { toastError, toastSuccess } from '@/store/toast'
import ConsentModal from '@/components/ConsentModal'

type PatientListResponse = { patients: Patient[] }

export default function VisitNewPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [params] = useSearchParams()
  const preselectedId = params.get('patientId')
  const user = useAuth((s) => s.user)

  const [patientId, setPatientId] = useState<number | ''>(
    preselectedId ? Number(preselectedId) : '',
  )
  const [visitType, setVisitType] = useState<VisitType>('initial')
  const [q, setQ] = useState('')
  const [consentPatient, setConsentPatient] = useState<Pick<Patient, 'id' | 'hn' | 'nameTh' | 'consentAudio' | 'consentAiScribe'> | null>(null)

  const { data: patientData } = useQuery<PatientListResponse>({
    queryKey: ['patients', q, 'picker'],
    queryFn: () => {
      const p = new URLSearchParams()
      if (q.trim()) p.set('q', q.trim())
      p.set('limit', '25')
      return api<PatientListResponse>(`/patients?${p.toString()}`)
    },
  })
  const patients = patientData?.patients ?? []

  const selectedPatient = useMemo(
    () => patients.find((p) => p.id === patientId) ?? null,
    [patients, patientId],
  )

  const createMut = useMutation({
    mutationFn: (body: CreateVisitRequest) =>
      api<{ visit: Visit }>('/visits', { method: 'POST', body }),
    onSuccess: (res) => {
      toastSuccess('สร้างการรักษาสำเร็จ')
      qc.invalidateQueries({ queryKey: ['visits'] })
      navigate(`/visits/${res.visit.id}/record`, { replace: true })
    },
    onError: (err: unknown) => {
      if (err instanceof ApiClientError && err.status === 428 && err.code === 'consent-required') {
        const p = err.details?.patient as Pick<Patient, 'id' | 'hn' | 'nameTh' | 'consentAudio' | 'consentAiScribe'> | undefined
        if (p) {
          setConsentPatient(p)
          return
        }
      }
      const msg = err instanceof ApiClientError ? err.message : 'สร้างไม่สำเร็จ'
      toastError(msg)
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (typeof patientId !== 'number') {
      toastError('กรุณาเลือกคนไข้')
      return
    }
    if (!user) {
      toastError('ยังไม่ได้ล็อกอิน')
      return
    }
    createMut.mutate({
      patientId,
      visitType,
    })
  }

  return (
    <div className="max-w-2xl mx-auto p-4">
      <form onSubmit={handleSubmit} className="card space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">เข้ารับการรักษาใหม่</h1>
          <Link to="/visits" className="text-sm text-slate-500 hover:text-brand-600">
            ← กลับ
          </Link>
        </div>

        <div>
          <label className="label">ประเภทการรักษา</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setVisitType('initial')}
              className={
                'flex-1 min-h-tap px-4 py-2 rounded-md border text-sm font-medium ' +
                (visitType === 'initial'
                  ? 'bg-brand-600 text-white border-brand-600'
                  : 'bg-white text-slate-700 border-slate-300')
              }
            >
              ครั้งแรก
            </button>
            <button
              type="button"
              onClick={() => setVisitType('followup')}
              className={
                'flex-1 min-h-tap px-4 py-2 rounded-md border text-sm font-medium ' +
                (visitType === 'followup'
                  ? 'bg-brand-600 text-white border-brand-600'
                  : 'bg-white text-slate-700 border-slate-300')
              }
            >
              ติดตามผล
            </button>
          </div>
        </div>

        <div>
          <label className="label" htmlFor="patient-search">ค้นหาคนไข้</label>
          <input
            id="patient-search"
            className="input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ชื่อ / HN"
          />
        </div>

        {selectedPatient && (
          <div className="rounded-md border border-brand-300 bg-brand-50 p-3 text-sm">
            <div className="font-medium text-slate-900">
              {selectedPatient.nameTh} <span className="font-mono text-xs text-slate-500">HN {selectedPatient.hn}</span>
            </div>
            {(!selectedPatient.consentAudio || !selectedPatient.consentAiScribe) && (
              <div className="text-xs text-rose-600 mt-1" role="alert">
                ⚠ คนไข้ยังไม่ยินยอม PDPA —{' '}
                <button
                  type="button"
                  className="underline font-medium hover:text-rose-800"
                  onClick={() => setConsentPatient(selectedPatient)}
                >
                  ขอความยินยอมตอนนี้
                </button>
              </div>
            )}
          </div>
        )}

        <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-md">
          {patients.length === 0 ? (
            <p className="text-sm text-slate-500 p-4 text-center">ไม่พบคนไข้</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {patients.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => setPatientId(p.id)}
                    className={
                      'w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ' +
                      (p.id === patientId ? 'bg-brand-50' : '')
                    }
                  >
                    <div className="font-medium">{p.nameTh}</div>
                    <div className="font-mono text-xs text-slate-500">
                      HN {p.hn}
                      {p.phone ? ` • ${p.phone}` : ''}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex gap-2 pt-2">
          <Link to="/visits" className="btn-ghost flex-1 text-center min-h-tap">
            ยกเลิก
          </Link>
          <button
            type="submit"
            className="btn-primary flex-1 min-h-tap disabled:opacity-50"
            disabled={createMut.isPending || typeof patientId !== 'number'}
          >
            {createMut.isPending ? 'กำลังสร้าง…' : 'สร้าง → อัดเสียง'}
          </button>
        </div>
      </form>

      {consentPatient && (
        <ConsentModal
          open
          onClose={() => setConsentPatient(null)}
          patient={consentPatient}
          onGranted={() => {
            setConsentPatient(null)
            // Retry visit creation now that consent is granted
            if (typeof patientId === 'number') {
              createMut.mutate({ patientId, visitType })
            }
          }}
        />
      )}
    </div>
  )
}
